import {
  UpdateEventListener,
  FireableUpdateEventListener,
} from 'utils/UpdateEventListener';
import _ from 'lodash';
import i18n from 'i18next';
import {
  DefaultCreativeListModel,
  CreativeListColumns,
  CreativeListModel,
} from './CampaignCreativeList/CreativeListModel';
import rtbBindingListFormatters from './CampaignCreativeList/defaultListFormatters';
import { CreativeManagementStateContext } from './CreativeManagementStateContext';
import { CampaignCreativeData } from './CampaignCreativeData';

export interface ModifyCreativeActivationStateContentModel {
  readonly state: ModifyCreativeActivationStateContentState;
  readonly event: UpdateEventListener<ModifyCreativeActivationStateContentModel>;
  readonly creativeData: CampaignCreativeData[];
  readonly activate: boolean;
  readonly selectedCreatives: Array<number>;
  setStateContext(stateContext: CreativeManagementStateContext): void;
  getTitle(): string;
  handleOnSelect(creativeId: number): void;
  handleOnSelectAll(page: number, sizePerPage: number): void;
  onCampaignChange(index: number): void;
  getCreativeListModel(): CreativeListModel;
}

export type ModifyCreativeActivationStateContentProps = {
  readonly model: ModifyCreativeActivationStateContentModel;
};

export type ModifyCreativeActivationStateContentState = {
  readonly selectedCreatives: Array<number>;
  readonly currentCampaignIndex: number;
};

abstract class DefaultModifyCreativeActivationStateContentModel
  implements ModifyCreativeActivationStateContentModel
{
  event: FireableUpdateEventListener<ModifyCreativeActivationStateContentModel>;
  creativeData: CampaignCreativeData[];
  stateContext?: CreativeManagementStateContext;
  activate: boolean;
  selectedCreatives: Array<number>;
  currentCampaignIndex: number;
  bindingIdMap: { [key: string]: string } = {};

  constructor(creativeData: CampaignCreativeData[], activate: boolean) {
    this.event =
      new FireableUpdateEventListener<ModifyCreativeActivationStateContentModel>();
    this.creativeData = creativeData.map((data) => {
      return {
        ...data,
        creatives: _.filter(
          data.creatives,
          (creative) => creative.isActiveBinding === !activate,
        ),
      };
    });
    this.activate = activate;
    this.selectedCreatives = [];
    this.currentCampaignIndex = 0;
    creativeData.forEach((data) => {
      data.creatives.forEach((creative) => {
        this.bindingIdMap[`${data.id}-${creative.id}`] = creative.l3ChannelId;
      });
    });
  }

  get state(): ModifyCreativeActivationStateContentState {
    return {
      selectedCreatives: this.selectedCreatives,
      currentCampaignIndex: this.currentCampaignIndex,
    };
  }

  abstract get listFormatters();

  getCreativeListModel() {
    const currentCreativeData = this.creativeData[this.currentCampaignIndex];
    const columnsToShow = [
      CreativeListColumns.ID,
      CreativeListColumns.PREVIEW,
      CreativeListColumns.STATUS,
      CreativeListColumns.DELIVERY,
      CreativeListColumns.TOOL,
    ];
    return new DefaultCreativeListModel(
      {
        id: currentCreativeData.id,
        startDate: currentCreativeData.startDate,
        endDate: currentCreativeData.endDate,
      },
      currentCreativeData.creatives,
      _.intersection(
        currentCreativeData.creatives.map((creative) => creative.id),
        this.selectedCreatives,
      ),
      this.handleOnSelect,
      this.handleOnSelectAll,
      (creativeId) => `/creatives/${creativeId}/edit`,
      this.listFormatters,
      columnsToShow,
    );
  }

  getTitle() {
    if (this.activate) {
      return i18n.t<string>(
        'modifyCreativeActivationStateContent.labels.chooseActivate',
      );
    } else {
      return i18n.t<string>(
        'modifyCreativeActivationStateContent.labels.chooseDeactivate',
      );
    }
  }

  setStateContext = (stateContext: CreativeManagementStateContext) => {
    this.stateContext = stateContext;
  };

  handleOnSelect = (creativeId: number) => {
    if (this.selectedCreatives.indexOf(creativeId) > -1) {
      _.remove(this.selectedCreatives, (id) => id === creativeId);
    } else {
      this.selectedCreatives.push(creativeId);
    }

    this.updateStateData();
    this.updateState();
  };

  handleOnSelectAll = (page: number, sizePerPage: number) => {
    const allCreativeId = this.creativeData[
      this.currentCampaignIndex
    ].creatives.map((creative) => creative.id);
    const offset = (page - 1) * sizePerPage;
    const end = offset + sizePerPage;
    const creatives = allCreativeId.slice(offset, end);
    const notSelected = _.difference(creatives, this.selectedCreatives);
    if (notSelected.length > 0) {
      this.selectedCreatives = _.concat(this.selectedCreatives, notSelected);
    } else {
      _.remove(this.selectedCreatives, (creativeId) =>
        creatives.includes(creativeId),
      );
    }
    this.updateStateData();
    this.updateState();
  };

  updateStateData = () => {
    this.stateContext?.setData &&
      this.stateContext.setData({
        bindingIdMap: this.bindingIdMap,
        activate: this.activate,
        selectedCreatives: this.selectedCreatives,
      });
  };

  onCampaignChange = (index: number) => {
    this.currentCampaignIndex = index;
    this.updateState();
  };

  updateState() {
    this.event.fireEvent(this);
  }
}

export class RtbModifyCreativeActivationStateContentModel extends DefaultModifyCreativeActivationStateContentModel {
  get listFormatters() {
    return rtbBindingListFormatters;
  }
}
